import React, { useState, useRef, useEffect } from "react";
import { 
  UserCheck, UserPlus, Building2, GraduationCap, Globe, BookOpen, 
  TrendingUp, Briefcase, Zap, Check, ChevronDown, Sparkles, Compass
} from "lucide-react";
import { 
  AUDIENCE_MODES, 
  AUDIENCE_MODE_DETAILS, 
  AudienceModeId, 
  normalizeAudienceModeId 
} from "../config/audienceModes";

interface AudienceSelectorProps {
  currentMode: AudienceModeId | string;
  onSelectMode: (modeId: AudienceModeId) => void;
  showLabel?: boolean;
  className?: string;
  isLoading?: boolean;
  variant?: string;
}

const ICON_MAP: Record<string, React.ElementType> = {
  UserCheck,
  UserPlus,
  Building2,
  GraduationCap,
  Globe,
  BookOpen,
  TrendingUp,
  Briefcase,
  Zap,
  Compass
};

// Concise, elegant subtitles for the dropdown items
const SHORT_SUBTITLES: Record<AudienceModeId, string> = {
  job_seeker: "Find businesses that may be hiring",
  business_explorer: "Discover local restaurants, shops & services",
  recruiter: "Find employers showing hiring activity",
  staffing_agency: "Find businesses with possible staffing demand",
  career_coach: "Curate job leads & target employers for clients",
  workforce_organization: "Track regional hiring activity across community districts",
  college_career_center: "Find entry-level employers & student internships",
  sales_professional: "Find B2B prospects & tech opportunity leads",
  business_development: "Identify strategic partnership & expansion targets",
  agency_freelancer: "Find businesses needing web design, SEO, & IT"
};

export const AudienceSelector: React.FC<AudienceSelectorProps> = ({
  currentMode,
  onSelectMode,
  showLabel = true,
  className = "",
  isLoading = false,
}) => {
  const modeIds = Object.keys(AUDIENCE_MODES) as AudienceModeId[];
  const [isOpen, setIsOpen] = useState(false);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState<number>(-1);
  const containerRef = useRef<HTMLDivElement>(null);

  const activeModeId = normalizeAudienceModeId(currentMode);
  const activeConfig = AUDIENCE_MODES[activeModeId] || AUDIENCE_MODES.job_seeker;
  const activeDetails = AUDIENCE_MODE_DETAILS[activeModeId] || AUDIENCE_MODE_DETAILS.job_seeker;
  const ActiveIcon = ICON_MAP[activeConfig.iconName] || UserCheck;

  // Handle clicking outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // When dropdown opens, initialize focus to active mode index
  useEffect(() => {
    if (isOpen) {
      const activeIdx = modeIds.indexOf(activeModeId);
      setFocusedIndex(activeIdx >= 0 ? activeIdx : 0);
    }
  }, [isOpen, activeModeId]);

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault();
        setIsOpen(true);
      }
      return;
    }

    if (e.key === "Escape") {
      e.preventDefault();
      setIsOpen(false);
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setFocusedIndex((prev) => (prev + 1) % modeIds.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setFocusedIndex((prev) => (prev - 1 + modeIds.length) % modeIds.length);
    } else if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      if (focusedIndex >= 0 && focusedIndex < modeIds.length) {
        handleSelect(modeIds[focusedIndex]);
      }
    }
  };

  const handleSelect = (modeId: AudienceModeId) => {
    onSelectMode(modeId);
    setIsOpen(false);
  };

  return (
    <div className={`relative w-full max-w-full text-left box-border ${className}`} ref={containerRef} onKeyDown={handleKeyDown}>
      {showLabel && (
        <label className="block text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1">
          Audience Mode
        </label>
      )}

      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        disabled={isLoading}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls="audience-mode-dropdown"
        aria-label={`Audience mode: current mode ${activeConfig.label}`}
        className="w-full flex items-center justify-between gap-2.5 px-3 py-2 rounded-xl bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 border border-slate-200 dark:border-slate-800 hover:border-indigo-300 dark:hover:border-indigo-700/60 shadow-2xs hover:shadow-xs transition-all cursor-pointer focus:outline-hidden focus:ring-2 focus:ring-indigo-500/30 text-xs font-medium group disabled:opacity-50 box-border"
      >
        <div className="flex items-center gap-2 truncate min-w-0">
          <div className="p-1 rounded-md bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400 group-hover:scale-105 transition-transform shrink-0">
            <ActiveIcon className="w-3.5 h-3.5" />
          </div>
          <span className="font-bold text-slate-800 dark:text-slate-100 truncate">
            {activeConfig.label}
          </span>
        </div>

        <div className="flex items-center gap-1 shrink-0 text-slate-400 dark:text-slate-500">
          <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${isOpen ? "rotate-180 text-indigo-600 dark:text-indigo-400" : ""}`} />
        </div>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div 
          id="audience-mode-dropdown"
          role="listbox"
          tabIndex={-1}
          aria-activedescendant={focusedIndex >= 0 ? `audience-option-${modeIds[focusedIndex]}` : undefined}
          className="absolute left-0 right-0 z-50 mt-1.5 w-full max-w-[calc(100vw-24px)] max-h-[min(380px,calc(100vh-140px))] overflow-y-auto overscroll-contain rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-xl p-1 space-y-0.5 focus:outline-hidden box-border divide-y divide-slate-100 dark:divide-slate-800/50"
        >
          <div className="py-0.5 space-y-0.5">
            {modeIds.map((modeId, index) => {
              const config = AUDIENCE_MODES[modeId];
              const Icon = ICON_MAP[config.iconName] || UserCheck;
              const isSelected = modeId === activeModeId;
              const isFocused = index === focusedIndex;
              const subtitle = SHORT_SUBTITLES[modeId] || config.tagline;

              return (
                <button
                  key={modeId}
                  id={`audience-option-${modeId}`}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  onClick={() => handleSelect(modeId)}
                  className={`w-full text-left px-2.5 py-2 rounded-lg transition-colors flex items-center justify-between gap-2 cursor-pointer box-border min-w-0 ${
                    isSelected
                      ? "bg-indigo-50/90 dark:bg-indigo-950/60 text-indigo-900 dark:text-indigo-100 border border-indigo-200/60 dark:border-indigo-800/50"
                      : isFocused
                      ? "bg-slate-100 dark:bg-slate-800 text-slate-800 dark:text-slate-100 border border-transparent"
                      : "hover:bg-slate-100/80 dark:hover:bg-slate-800/70 text-slate-700 dark:text-slate-200 border border-transparent"
                  }`}
                >
                  <div className="flex items-start gap-2 min-w-0 flex-1">
                    <div className={`p-1.5 rounded-md shrink-0 mt-0.5 ${
                      isSelected 
                        ? "bg-indigo-600 text-white" 
                        : "bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400"
                    }`}>
                      <Icon className="w-3.5 h-3.5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className={`text-xs font-bold truncate ${
                        isSelected ? "text-indigo-950 dark:text-indigo-100" : "text-slate-800 dark:text-slate-100"
                      }`}>
                        {config.label}
                      </div>
                      <div className="text-[11px] text-slate-500 dark:text-slate-400 leading-snug line-clamp-2 break-words">
                        {subtitle}
                      </div>
                    </div>
                  </div>

                  {isSelected && (
                    <Check className="w-4 h-4 text-indigo-600 dark:text-indigo-400 shrink-0 self-center ml-1" />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Secondary Collapsible Details Toggle Button */}
      <div className="mt-1.5">
        <button
          type="button"
          onClick={() => setIsDetailsOpen(!isDetailsOpen)}
          aria-expanded={isDetailsOpen}
          aria-controls="audience-mode-details"
          className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-slate-500 dark:text-slate-400 hover:text-indigo-600 dark:hover:text-indigo-400 focus:outline-hidden focus:ring-2 focus:ring-indigo-500/30 rounded px-1 py-0.5 transition-colors cursor-pointer"
        >
          <span>What does this mode change?</span>
          <ChevronDown
            className={`w-3.5 h-3.5 transition-transform duration-200 ${
              isDetailsOpen ? "rotate-180 text-indigo-600 dark:text-indigo-400" : ""
            }`}
          />
        </button>

        {/* Details Panel */}
        {isDetailsOpen && (
          <div
            id="audience-mode-details"
            role="region"
            aria-label="Audience mode details"
            className="mt-1.5 p-3 rounded-xl bg-slate-50/90 dark:bg-slate-800/40 border border-slate-200/80 dark:border-slate-800/80 text-xs space-y-2 text-slate-700 dark:text-slate-200 transition-all shadow-2xs"
          >
            <div className="font-bold text-slate-900 dark:text-slate-100 text-xs">
              {activeDetails.title}
            </div>
            <p className="text-[11.5px] leading-relaxed text-slate-600 dark:text-slate-400">
              {activeDetails.description}
            </p>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-1">
                Changes in this mode:
              </div>
              <ul className="space-y-1 text-[11px] text-slate-600 dark:text-slate-300 pl-3.5 list-disc marker:text-indigo-500/80">
                {activeDetails.changes.map((item, idx) => (
                  <li key={idx} className="leading-snug">
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
